import {useState} from 'react'
import {useTranslation} from 'react-i18next'
import {
  openFormaPdf,
  useAgregarPersona,
  useRetirarPaquete,
  type BateriaForma,
  type BateriaPaquete,
} from '@/hooks/bateria/use-bateria'
import {useCambiarForma} from '@/hooks/bateria/use-bateria-ciclo-vida'
import {useEmployee} from '@/hooks/empresa/identidad/use-employees'
import {Button} from '@/components/ui/button'
import {Dialog, DialogContent, DialogHeader, DialogTitle} from '@/components/ui/dialog'
import {Label} from '@/components/ui/label'
import {FormDialogFooter} from '@/components/form-dialog-footer'
import {PorQue} from '@/components/ui/por-que'
import {
  IDENTITY_VALIDATORS,
  PersonaBasicFields,
  clearChangedIdentityErrors,
  validateBasicIdentity,
  type PersonaIdentity,
} from '@/components/persona-identity-fields'
import {claveDeError, marcarErrorComoComunicado, translateApiError} from '@/lib/errors'

const FORMAS: BateriaForma[] = ['A', 'B']

/** Punto de partida editable, nunca la verdad: «Ana María López» no dice dónde empieza el apellido. */
function partirNombre(nombreCompleto: string): {name: string; last_name: string} {
  const palabras = nombreCompleto.trim().split(/\s+/).filter(Boolean)
  const corte = Math.ceil(palabras.length / 2)
  return {name: palabras.slice(0, corte).join(' '), last_name: palabras.slice(corte).join(' ')}
}

/** Envoltura con nombre: `doctrina_de_ui` (R4) busca `function validate*`; la regla vive en `persona-identity-fields.tsx`. */
function validateCadenaIdentidad(identity: PersonaIdentity) {
  return validateBasicIdentity(identity)
}

type Fase = 'forma' | 'guarda' | 'cadena' | 'exito'

/**
 * G26 (v2 §2.5). Con páginas leídas, el 409 `paginas_leidas` encadena retirar → agregar → imprimir:
 * nunca reescribe la historia de un papel ya llenado.
 */
export function CambiarFormaDialog({
  businessId,
  evaluacionId,
  paquete,
  open,
  onOpenChange,
}: {
  businessId: string
  evaluacionId: string
  paquete: BateriaPaquete
  open: boolean
  onOpenChange: (open: boolean) => void
}) {
  const {t} = useTranslation()
  const cambiar = useCambiarForma(businessId, evaluacionId)
  const retirar = useRetirarPaquete(businessId, evaluacionId)
  const agregar = useAgregarPersona(businessId, evaluacionId)
  const otraForma = paquete.forma === 'A' ? 'B' : 'A'
  // Ref de callback: Radix monta el contenido después del primer efecto y `useRef` daría `null`.
  const [nodoForma, setNodoForma] = useState<HTMLElement | null>(null)
  const [fase, setFase] = useState<Fase>('forma')
  // La cadena re-agrega a la misma persona: su área/cargo/sede se reusan del Employee sin pedirlos.
  const {data: empleadoActual} = useEmployee(businessId, fase === 'cadena' ? paquete.employee_id : undefined)
  const [forma, setForma] = useState<BateriaForma>(otraForma)
  const [identity, setIdentity] = useState<PersonaIdentity>(() => ({
    ...partirNombre(paquete.employee_name),
    cedula: paquete.employee_cedula,
    tipo_documento: paquete.employee_tipo_documento ?? 'CC',
    email: '',
    phone: '',
  }))
  const [errors, setErrors] = useState<Partial<Record<keyof PersonaIdentity, string>>>({})
  const [error, setError] = useState<string | null>(null)
  const [nuevoPaqueteId, setNuevoPaqueteId] = useState<string | null>(null)
  const [printError, setPrintError] = useState<string | null>(null)

  function close() {
    setFase('forma')
    setForma(otraForma)
    setError(null)
    setNuevoPaqueteId(null)
    setPrintError(null)
    cambiar.reset()
    retirar.reset()
    agregar.reset()
    onOpenChange(false)
  }

  function submitForma(e: React.FormEvent) {
    e.preventDefault()
    setError(null)
    cambiar.mutate(
      {paqueteId: paquete.id, forma},
      {
        onSuccess: close,
        onError: (err) => {
          const detail = (err as {response?: {data?: {detail?: unknown}}})?.response?.data?.detail
          if (detail && typeof detail === 'object' && (detail as {motivo?: string}).motivo === 'paginas_leidas') {
            marcarErrorComoComunicado(err)
            setFase('cadena')
            return
          }
          // Sin `translateApiError`: marcaría cualquier error, inesperado incluido, y la red global dejaría de reportarlo.
          setError(t(claveDeError(err)))
        },
      }
    )
  }

  async function submitCadena(e: React.FormEvent) {
    e.preventDefault()
    setError(null)
    const found = validateCadenaIdentidad(identity)
    setErrors(found)
    if (Object.keys(found).length > 0) return
    try {
      await retirar.mutateAsync({paqueteId: paquete.id, motivo: 'error_de_alta'})
      const nuevo = await agregar.mutateAsync({
        documento: identity.cedula.trim(),
        tipo_documento: identity.tipo_documento,
        nombres: identity.name.trim(),
        apellidos: identity.last_name.trim(),
        area: empleadoActual?.areas[0]?.name ?? '',
        cargo: empleadoActual?.positions[0]?.name ?? '',
        sede: empleadoActual?.sede?.name ?? '',
        forma,
      })
      setNuevoPaqueteId(nuevo.id)
      setFase('exito')
    } catch (err) {
      setError(translateApiError(err))
    }
  }

  async function imprimir() {
    if (!nuevoPaqueteId) return
    setPrintError(null)
    try {
      await openFormaPdf(businessId, nuevoPaqueteId)
    } catch (err) {
      setPrintError(translateApiError(err))
    }
  }

  return (
    <Dialog open={open} onOpenChange={(next) => !next && close()}>
      <DialogContent
        ref={setNodoForma}
        className="max-h-[85vh] max-w-lg overflow-y-auto"
        data-testid="cambiar-forma-dialog">
        <DialogHeader>
          <DialogTitle>{t('admin.bateria.cambiarForma.title')}</DialogTitle>
        </DialogHeader>

        {fase === 'forma' && (
          <form onSubmit={submitForma} className="space-y-4" noValidate>
            <p className="text-sm text-muted-foreground">
              {t('admin.bateria.cambiarForma.intro', {nombre: paquete.employee_name, actual: paquete.forma})}
            </p>
            <div className="space-y-2">
              <Label>{t('admin.bateria.cambiarForma.nuevaForma')}</Label>
              <div className="flex flex-wrap gap-4">
                {FORMAS.filter((f) => f !== paquete.forma).map((value) => (
                  <label key={value} className="flex items-center gap-2 text-sm text-foreground">
                    <input
                      type="radio"
                      name="cambiar-forma-nueva"
                      checked={forma === value}
                      onChange={() => setForma(value)}
                      data-testid={`cambiar-forma-${value}`}
                      className="accent-primary"
                    />
                    {t('admin.bateria.formaValue', {forma: value})}
                  </label>
                ))}
              </div>
            </div>
            <ul className="notice-amber list-disc space-y-1 rounded-lg py-2.5 pr-4 pl-8 text-sm">
              <li>{t('admin.bateria.cambiarForma.consecuenciaQr')}</li>
              <li>{t('admin.bateria.cambiarForma.consecuenciaBaremos')}</li>
              <li>{t('admin.bateria.cambiarForma.consecuenciaReimprimir')}</li>
            </ul>
            <FormDialogFooter
              error={error}
              pending={cambiar.isPending}
              submitLabel={t('admin.bateria.cambiarForma.submit')}
              onCancel={close}
            />
          </form>
        )}

        {fase === 'cadena' && (
          <form onSubmit={submitCadena} className="space-y-4" noValidate>
            <div className="notice-amber rounded-lg px-4 py-2.5 text-sm" data-testid="cambiar-forma-guarda">
              {t('admin.bateria.cambiarForma.guardaPaginasLeidas')}{' '}
              <PorQue
                label={t('admin.bateria.cambiarForma.porqueLabel')}
                contenedor={nodoForma}
                testId="cambiar-forma-porque"
                contenidoTestId="cambiar-forma-porque-contenido">
                {t('admin.bateria.cambiarForma.porque')}
              </PorQue>
            </div>
            <p className="text-sm text-muted-foreground">{t('admin.bateria.cambiarForma.cadenaIntro')}</p>
            <PersonaBasicFields
              value={identity}
              onChange={(next) => {
                setErrors((prev) => clearChangedIdentityErrors(prev, next, identity))
                setIdentity(next)
              }}
              tKey="admin.bateria.cambiarForma"
              testPrefix="cambiar-forma-cadena"
              errors={errors}
              onBlurField={(field) =>
                setErrors((prev) => ({
                  ...prev,
                  [field]: IDENTITY_VALIDATORS[field](identity[field], identity) ?? undefined,
                }))
              }
            />
            <p className="text-xs text-muted-foreground">
              {t('admin.bateria.cambiarForma.cadenaFormaNota', {forma: t('admin.bateria.formaValue', {forma})})}
            </p>
            <FormDialogFooter
              error={error}
              pending={retirar.isPending || agregar.isPending}
              submitLabel={t('admin.bateria.cambiarForma.cadenaSubmit')}
              onCancel={close}
            />
          </form>
        )}

        {fase === 'exito' && (
          <div className="space-y-4">
            <p className="text-sm text-foreground">{t('admin.bateria.cambiarForma.exito')}</p>
            {printError && <p className="text-xs text-destructive">{printError}</p>}
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={close}>
                {t('common.close')}
              </Button>
              <Button data-testid="cambiar-forma-imprimir" onClick={imprimir}>
                {t('admin.bateria.cambiarForma.imprimirSuForma')}
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  )
}
